import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Container, Section, Eyebrow, DisplayHeading, BodyCopy, Reveal } from "@/components/kit";
import { MeiraMark } from "@/components/Logo";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const INPUTS = ["AI Systems", "Models", "Agents"];

const CONTROLS = [
  { label: "Security Testing", path: ["Security Testing", "Finding", "Control", "Evidence Core", "Trust Graph", "Certificate"] },
  { label: "AI Inventory", path: ["AI Inventory", "Ownership", "Evidence Core", "Trust Passport"] },
  { label: "Risk", path: ["Risk", "Control", "Evidence Core", "Assurance"] },
  { label: "Policies", path: ["Policies", "Control", "Evidence Core", "Audit Trail"] },
  { label: "Frameworks", path: ["Framework", "Control", "Evidence", "Readiness", "Assurance"] },
  { label: "Controls", path: ["Control", "Evidence Core", "Trust Graph", "Certificate"] },
  { label: "Audit Trail", path: ["Audit Trail", "Evidence Core", "Assurance", "Verified"] },
];

const TRUST = ["Trust Graph", "Trust Passport", "Assurance Dashboard", "Certificates"];
const EXTERNAL = ["Customer", "Procurement", "Auditor", "Board", "Regulator"];
const PERSPECTIVES = ["ISO/IEC 42001", "EU AI Act", "NIST AI RMF", "UAE PDPL"];

function Connector() {
  return <div className="mx-auto my-3 h-6 w-px bg-gradient-to-b from-line-bright to-line-soft" />;
}

export default function EvidenceCore() {
  const [active, setActive] = useState(null);
  const activeCtrl = CONTROLS.find((c) => c.label === active);

  return (
    <Section id="architecture" className="border-t border-line">
      <Container>
        <div className="max-w-3xl">
          <Reveal><Eyebrow tone="brand">Meira Trust Architecture</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 text-3xl leading-[1.08] sm:text-4xl md:text-5xl">
            One trust layer for your <span className="text-gradient">entire AI estate.</span>
          </DisplayHeading>
          <Reveal i={1}>
            <BodyCopy className="mt-6">
              Testing doesn’t live apart from governance. Governance doesn’t live apart from
              assurance. Everything writes to one shared evidence base — so a single finding can
              travel all the way to verifiable proof.
            </BodyCopy>
          </Reveal>
        </div>

        <div className="mt-10 grid gap-5 sm:mt-14 sm:gap-6 lg:grid-cols-[1.5fr_1fr] lg:items-start">
          {/* architecture stack */}
          <div className="relative rounded-lg border border-line bg-panel-raised/40 p-4 tech-grid sm:p-9">
            {/* input layer */}
            <LayerLabel>System layer</LayerLabel>
            <div className="flex flex-wrap justify-center gap-2">
              {INPUTS.map((c) => <Chip key={c} tone="brand">{c}</Chip>)}
            </div>
            <Connector />

            {/* control layer (interactive) */}
            <LayerLabel>Control &amp; assurance layer</LayerLabel>
            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="flex flex-wrap justify-center gap-2">
              {CONTROLS.map((c) => {
                const inPath = activeCtrl?.path.includes(c.label);
                return (
                  <motion.button
                    key={c.label}
                    variants={fadeUp}
                    data-testid={`arch-control-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                    onMouseEnter={() => setActive(c.label)}
                    onFocus={() => setActive(c.label)}
                    onMouseLeave={() => setActive(null)}
                    onClick={() => setActive((a) => (a === c.label ? null : c.label))}
                    aria-pressed={active === c.label}
                    className={`min-h-[38px] rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-300 focus:outline-none ${
                      active === c.label ? "border-brand/60 bg-brand/15 text-content" : active && !inPath ? "border-line-soft text-content-faint" : "border-line text-content-muted hover:text-content"
                    }`}
                  >
                    {c.label}
                  </motion.button>
                );
              })}
            </motion.div>
            <Connector />

            {/* evidence core */}
            <div className="relative flex justify-center py-4">
              {/* incoming evidence signals */}
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="absolute top-0 h-1.5 w-1.5 rounded-full bg-gold"
                  style={{ left: `${32 + i * 18}%` }}
                  animate={{ y: [0, 34], opacity: [0, 1, 0] }}
                  transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.6, ease: "easeIn" }}
                />
              ))}
              <div className="relative flex h-32 w-32 flex-col items-center justify-center rounded-full border-2 border-gold/50 bg-ink gold-glow sm:h-40 sm:w-40">
                <span className="absolute -inset-3 rounded-full border border-gold/15" />
                <span className="absolute inset-0 animate-pulse-ring rounded-full bg-gold/25" />
                <MeiraMark size={26} className="sm:hidden" />
                <MeiraMark size={30} className="hidden sm:block" />
                <span className="mt-2 text-center font-mono text-[10px] uppercase leading-tight tracking-[0.18em] text-gold">Evidence<br />Core</span>
              </div>
            </div>
            {/* framework perspectives */}
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {PERSPECTIVES.map((p) => (
                <span key={p} className="rounded-full border border-line bg-panel-raised px-2.5 py-1 font-mono text-[10px] tracking-wide text-content-muted">{p}</span>
              ))}
            </div>
            <Connector />

            {/* trust layer */}
            <LayerLabel>Trust layer</LayerLabel>
            <div className="flex flex-wrap justify-center gap-2">
              {TRUST.map((c) => <Chip key={c} tone="gold">{c}</Chip>)}
            </div>
            <Connector />

            {/* external proof */}
            <LayerLabel>External proof</LayerLabel>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {EXTERNAL.map((c) => <Chip key={c}>{c}</Chip>)}
              <span className="text-content-faint">→</span>
              <span className="rounded-full border border-valid/30 bg-valid/10 px-3.5 py-1.5 font-mono text-[12px] uppercase tracking-[0.14em] text-valid valid-glow">Verified</span>
            </div>
          </div>

          {/* path readout */}
          <div className="lg:sticky lg:top-28">
            <div className="rounded-lg border border-line bg-panel-raised p-5 sm:p-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-content-faint">Trace a path</div>
              <AnimatePresence mode="wait">
                {activeCtrl ? (
                  <motion.div key={activeCtrl.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="mt-4">
                    <h4 className="font-display text-xl text-content">{activeCtrl.label}</h4>
                    <div className="mt-4 flex flex-col gap-2">
                      {activeCtrl.path.map((step, i) => (
                        <div key={step} className="flex items-center gap-3">
                          <span className={`h-1.5 w-1.5 rounded-full ${i === activeCtrl.path.length - 1 ? "bg-valid" : "bg-brand-soft"}`} />
                          <span className="text-sm text-content">{step}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  <motion.p key="hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 text-sm leading-relaxed text-content-muted">
                    <span className="lg:hidden">Tap</span><span className="hidden lg:inline">Hover</span> a capability in the
                    control layer to see how it connects — from a single control all the way to
                    verifiable proof through the Evidence Core.
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
            <p className="mt-4 text-[13px] leading-relaxed text-content-muted">
              One evidence base, many assurance perspectives. Security testing additionally maps to
              relevant OWASP LLM guidance.
            </p>
            <p className="mt-3 font-mono text-[11px] leading-relaxed tracking-wide text-content-muted">
              Meira does not itself issue formal ISO/IEC 42001 certification. Formal certification is
              issued independently by an accredited certification body.
            </p>
          </div>
        </div>
      </Container>
    </Section>
  );
}

function LayerLabel({ children }) {
  return <div className="mb-3 text-center font-mono text-[9px] uppercase tracking-[0.24em] text-content-faint">{children}</div>;
}

function Chip({ children, tone }) {
  const cls = tone === "brand" ? "border-brand/25 text-brand-soft" : tone === "gold" ? "border-gold/25 text-gold" : "border-line text-content-muted";
  return <span className={`rounded-full border bg-panel px-3.5 py-1.5 text-[13px] font-medium ${cls}`}>{children}</span>;
}
